import api from './api';

export interface Bill {
  id: string;
  bill_number: string;
  customer_id: string;
  package_id: string;
  billing_period_start: string;
  billing_period_end: string;
  due_date: string;
  amount: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  payment_date?: string;
  payment_method?: string;
  notes?: string;
  created_by?: string;
  paid_by?: string;
  created_at: string;
  updated_at: string;
  customer?: {
    id: string;
    name: string;
    customer_code: string;
    username: string;
    email: string;
    phone: string;
  };
  package?: {
    id: string;
    name: string;
    price: number;
    bandwidth_download: string;
    bandwidth_upload: string;
  };
  creator?: {
    id: string;
    username: string;
    email: string;
  };
  paidBy?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface BillFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  customer_id?: string;
  month?: string;
  year?: string;
  spop_id?: string;
}

export interface CreateBillData {
  customer_id: string;
  package_id?: string;
  billing_period_start: string;
  billing_period_end: string;
  amount: number;
  tax_amount?: number;
  discount_amount?: number;
  notes?: string;
}

export interface GenerateMonthlyBillsData {
  month: number;
  year: number;
}

export interface BillStats {
  pending: { count: number; total_amount: number };
  paid: { count: number; total_amount: number };
  overdue: { count: number; total_amount: number };
  cancelled: { count: number; total_amount: number };
}

export interface BillingSummary {
  total_bills: number;
  pending_bills: number;
  paid_bills: number;
  overdue_bills: number;
  total_amount: number;
  pending_amount: number;
  overdue_amount: number;
  last_payment_date?: string;
  next_bill_date?: string;
}

export interface DashboardStats {
  monthly: BillStats;
  today_due: number;
  today_due_amount: number;
  total_overdue: number;
}

export interface BillingPeriodData {
  customer_id: string;
  activation_date: string;
  billing_period: {
    month: number;
    year: number;
    start: string;
    end: string;
    due_date: string;
  };
}

class BillService {
  // Get all bills with filters
  async getBills(filters: BillFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/bills?${params.toString()}`);
    return response.data;
  }

  // Get bill by ID
  async getBillById(id: string) {
    const response = await api.get(`/bills/${id}`);
    return response.data;
  }

  // Create new bill
  async createBill(data: CreateBillData) {
    const response = await api.post('/bills', data);
    return response.data;
  }

  // Update bill
  async updateBill(id: string, data: Partial<CreateBillData>) {
    const response = await api.put(`/bills/${id}`, data);
    return response.data;
  }

  // Mark bill as paid
  async markBillAsPaid(id: string, data: { payment_method?: string; notes?: string }) {
    const response = await api.patch(`/bills/${id}/mark-paid`, data);
    return response.data;
  }

  // Delete bill
  async deleteBill(id: string) {
    const response = await api.delete(`/bills/${id}`);
    return response.data;
  }

  // Generate monthly bills
  async generateMonthlyBills(data: GenerateMonthlyBillsData) {
    const response = await api.post('/bills/generate-monthly', data);
    return response.data;
  }

  // Run automatic billing
  async runAutomaticBilling() {
    const response = await api.post('/bills/generate-automatic');
    return response.data;
  }

  // Mark overdue bills
  async markOverdueBills() {
    const response = await api.post('/bills/mark-overdue');
    return response.data;
  }

  // Get billing statistics
  async getBillingStats(month?: string, year?: string) {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    
    const response = await api.get(`/bills/stats?${params.toString()}`);
    return response.data;
  }

  // Get dashboard statistics (alias ke /bills/stats)
  async getDashboardStats() {
    return this.getBillingStats();
  }

  // Get overdue bills
  async getOverdueBills() {
    const response = await api.get('/bills/overdue');
    return response.data;
  }

  // Get customer billing summary
  async getCustomerBillingSummary(customerId: string) {
    const response = await api.get(`/bills/customer/${customerId}/summary`);
    return response.data;
  }

  // Get customer bills
  async getCustomerBills(customerId: string, filters: BillFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/customers/${customerId}/bills?${params.toString()}`);
    return response.data;
  }

  // Get billing period for customer
  async getBillingPeriod(customerId: string, month: number, year: number): Promise<{ success: boolean; data: BillingPeriodData }> {
    const params = new URLSearchParams();
    params.append('customer_id', customerId);
    params.append('month', month.toString());
    params.append('year', year.toString());

    const response = await api.get(`/bills/billing-period?${params.toString()}`);
    return response.data;
  }

  // WhatsApp notification methods
  async sendBillReminder(billId: string): Promise<any> {
    const response = await api.post(`/bills/${billId}/send-reminder`);
    return response.data;
  }

  async sendOverdueNotice(billId: string): Promise<any> {
    const response = await api.post(`/bills/${billId}/send-overdue-notice`);
    return response.data;
  }

  async sendBulkReminders(billIds: string[]): Promise<any> {
    const response = await api.post('/bills/send-bulk-reminders', { billIds });
    return response.data;
  }

  // Manual Bill Job Methods
  async getBillJobStats(): Promise<any> {
    const response = await api.get('/bill-jobs/stats');
    return response.data;
  }

  async processOverdueBills(): Promise<any> {
    const response = await api.post('/bill-jobs/process-overdue');
    return response.data;
  }

  async processCustomerReactivation(): Promise<any> {
    const response = await api.post('/bill-jobs/process-reactivation');
    return response.data;
  }

  async runAllBillJobs(): Promise<any> {
    const response = await api.post('/bill-jobs/run-all');
    return response.data;
  }

  async downloadInvoice(billId: string, billNumber?: string): Promise<void> {
    const response = await api.get(`/bills/${billId}/invoice`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `Invoice_${billNumber || billId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async exportBills(filters: BillFilters): Promise<void> {
    const params = new URLSearchParams();
    
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.year) {
      params.append('year', filters.year);
    }
    if (filters.spop_id) {
      params.append('spop_id', filters.spop_id);
    }
    
    let url = '/bills/export';
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Generate filename with timestamp
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '');
    let filename = `bills_export_${timestamp}.xlsx`;
    
    // Add filter info to filename
    if (filters.search || filters.status || filters.month || filters.year || filters.spop_id) {
      let filterInfo = '';
      if (filters.search) filterInfo += '_search';
      if (filters.status) filterInfo += `_${filters.status}`;
      if (filters.month) filterInfo += `_month${filters.month}`;
      if (filters.year) filterInfo += `_${filters.year}`;
      if (filters.spop_id) filterInfo += `_spop${filters.spop_id}`;
      filename = `bills_export${filterInfo}_${timestamp}.xlsx`;
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export default new BillService();
